// Real member payment data (additional area / additional flat, Scheme I & II)
// served by the backend: GET /payments/my (own rows) and /payments/all (admin).
// Figures are "as on 31.03.2026" from the audited books of accounts.
import axios from 'axios'
import config from '../../config'

const authHeaders = () => ({ Authorization: localStorage.getItem('token') })

// one shared request per page load for "my" data (alert, sidebar badge, page)
let myRequest = null
export const fetchMyPayments = ({ force = false } = {}) => {
  if (!myRequest || force) {
    myRequest = axios
      .get(`${config.apiUrl}/payments/my`, { headers: authHeaders() })
      .then(res => ({ asOn: res.data?.asOn, entries: res.data?.data || [] }))
      .catch(err => {
        myRequest = null // allow a retry later
        throw err
      })
  }
  return myRequest
}

export const fetchAllPayments = () =>
  axios
    .get(`${config.apiUrl}/payments/all`, { headers: authHeaders() })
    .then(res => ({ asOn: res.data?.asOn, totals: res.data?.totals || {}, entries: res.data?.data || [] }))

// ---- calculations ----
const TOLERANCE = 1 // ₹1 rounding differences in the books

// Split the total received across the 4 instalments in order
export const instalmentBreakdown = entry => {
  const amounts = [entry.installment1, entry.installment2, entry.installment3, entry.installment4]
  let remaining = entry.totalReceived || 0
  return amounts.map((amount, i) => {
    const paid = Math.max(0, Math.min(amount, remaining))
    remaining -= paid
    let status = 'due'
    if (amount <= 0) status = 'na'
    else if (paid >= amount - TOLERANCE) status = 'paid'
    else if (paid > 0) status = 'partial'
    return { no: i + 1, amount, paid, balance: Math.max(0, amount - paid), status }
  })
}

export const summarize = entries => ({
  totalPayable: entries.reduce((s, e) => s + (e.totalPayable || 0), 0),
  totalReceived: entries.reduce((s, e) => s + (e.totalReceived || 0), 0),
  outstanding: entries.reduce((s, e) => s + (e.outstanding || 0), 0),
  advance: entries.reduce((s, e) => s + (e.advance || 0), 0),
})

// outstanding below ₹1 is a rounding difference, not a real due
export const hasDue = entry => (entry?.outstanding || 0) >= TOLERANCE

// ---- formatting ----
export const formatINR = value =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(value || 0)

export const formatDate = date =>
  new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

// ---- admin edit + receipts (all go through the backend) ----
const base = () => `${config.apiUrl}/payments`

// PUT /payments/:id (admin) -> updated entry
export const updatePayment = (id, body) =>
  axios.put(`${base()}/${id}`, body, { headers: authHeaders() }).then(res => res.data?.data)

// Sends the file to /upload/image, then attaches its link to the payment.
// Returns the new receipt.
export const uploadReceipt = async (paymentId, file) => {
  const formData = new FormData()
  formData.append('upload', file)
  const up = await axios.post(`${config.apiUrl}/upload/image`, formData, { headers: authHeaders() })
  const url = up.data?.data
  if (!url) throw new Error('No file URL returned')
  const res = await axios.post(
    `${base()}/${paymentId}/receipts`,
    { FileUrl: url, FileName: file.name },
    { headers: authHeaders() }
  )
  return res.data?.data
}

export const deleteReceipt = receiptId =>
  axios.delete(`${base()}/receipts/${receiptId}`, { headers: authHeaders() })

// make the next fetchMyPayments() hit the server again (after edits / uploads)
export const refreshMyPayments = () => fetchMyPayments({ force: true })
